import API from '@apis/client';
import { AxiosResponse } from 'axios';
import {
  createSaleSelfPayload,
  createSaleSelfReRequestPayload,
  downloadSaleFilePayload,
  getSaleSelfPaylaod,
  getSaleSelfPayload,
} from 'models/Supplement';
import {
  QuotationSaleGetSaleQuotationCompanyData,
  QuotationSaleGetSaleQuotationCompanyError,
  QuotationSaleGetSaleQuotationSelfData,
  QuotationSaleGetSaleQuotationSelfError,
  QuotationSaleSelfCreateData,
  QuotationSaleSelfCreateError,
  QuotationSaleSelfDeleteSaleQuotationSelfData,
  QuotationSaleSelfDeleteSaleQuotationSelfError,
  QuotationSaleSelfGetSaleQuotationItemSelfData,
  QuotationSaleSelfGetSaleQuotationItemSelfError,
  QuotationSaleSelfGetSaleQuotationTotalPriceSelfData,
  QuotationSaleSelfGetSaleQuotationTotalPriceSelfError,
  QuotationSaleSelfPostSaleQuotationReRequestData,
  QuotationSaleSelfPostSaleQuotationReRequestError,
  QuotationSaleSelfReadData,
  QuotationSaleSelfReadError,
} from 'models/swagger/data-contracts';

/**
 *  판매 견적 요청서 생성 API
 *  @function createSaleSelfApi
 *  @param {number} rfq_number_id rfq number id
 *  @param {string} req_close_at 견적 유효기간
 *  @param {string} req_delivery_at 납기 일자
 *  @param {string} shipping_area 상차지
 *  @param {string} terms_of_payment 결제조건
 *  @param {string} additional_request_file 첨부파일
 *  @param {string} additional_request_file_name 첨부파일 명
 *  @param {string} seller_note 비고
 *  @param {createSaleSelfItemType[]} items 견적 품목 리스트
 */
export const createSaleSelfApi = ({
  rfq_number_id,
  req_close_at,
  req_delivery_at,
  shipping_area,
  terms_of_payment,
  additional_request_file,
  additional_request_file_name,
  seller_note,
  items,
  req_close_days,
  req_delivery_days,
}: createSaleSelfPayload): Promise<AxiosResponse<QuotationSaleSelfCreateData, QuotationSaleSelfCreateError>> => {
  return API.post(`/quotation/sale/self/${rfq_number_id}`, {
    req_close_at,
    req_delivery_at,
    shipping_area,
    terms_of_payment,
    additional_request_file,
    additional_request_file_name,
    seller_note,
    items,
    req_close_days,
    req_delivery_days,
  });
};
/**
 *  자신의 판매 견적 요청서 가져오기 API (프로젝트 관리)
 *  @function getSaleSelfApi
 *  @param {number} page 페이지
 *  @param {string} project_name 프로젝트 명
 *  @param {string} rfq_number 접수번호
 *  @param {boolean} finish 완료 여부
 */
export const getSaleSelfApi = ({
  page,
  finish,
  project_name,
  rfq_number,
  is_company_project,
}: getSaleSelfPayload): Promise<
  AxiosResponse<QuotationSaleGetSaleQuotationSelfData, QuotationSaleGetSaleQuotationSelfError>
> => {
  return API.get(
    `/quotation/sale/self?page=${page}&finish=${finish}&project_name=${project_name}&rfq_number=${rfq_number}&is_company_project=${is_company_project}`,
  );
};

/**
 *  판매자 - 회사의 판매 견적 요청서 가져오기 API (프로젝트 관리)
 *  @function getSaleCompanyApi
 *  @param {number} page 페이지
 *  @param {string} project_name 프로젝트 명
 *  @param {string} rfq_number 접수번호
 *  @param {boolean} finish 완료 여부
 */
export const getSaleCompanyApi = ({
  page,
  finish,
  project_name,
  rfq_number,
}: getSaleSelfPayload): Promise<
  AxiosResponse<QuotationSaleGetSaleQuotationCompanyData, QuotationSaleGetSaleQuotationCompanyError>
> => {
  return API.get(
    `/quotation/sale/company?page=${page}&finish=${finish}&project_name=${project_name}&rfq_number=${rfq_number}`,
  );
};

/**
 *  자신의 판매 견적 요청서 상세 가져오기 API (구매용 견적서, 프로젝트 관리 detail에서는 saleDetail을 보여줌)
 *  @function getSaleSelfDetailApi
 *  @param {number} rfq_number_id rfq number id
 */
export const getSaleSelfDetailApi = ({
  rfq_number_id,
  sale_quotation_id,
}: getSaleSelfPaylaod): Promise<AxiosResponse<QuotationSaleSelfReadData, QuotationSaleSelfReadError>> => {
  return API.get(`/quotation/sale/self/${rfq_number_id}?sale_quotation_id=${sale_quotation_id}`);
};

/**
 *  자신의 판매 견적 요청서 자재 리스트 가져오기 API
 *  @function getSaleSelfItemApi
 *  @param {number} rfq_number_id rfq number id
 */
export const getSaleSelfItemApi = ({
  rfq_number_id,
  sale_quotation_id
}: getSaleSelfPaylaod): Promise<
  AxiosResponse<QuotationSaleSelfGetSaleQuotationItemSelfData, QuotationSaleSelfGetSaleQuotationItemSelfError>
> => {
  return API.get(`/quotation/sale/self/${rfq_number_id}/item?sale_quotation_id=${sale_quotation_id}`);
};

/**
 *  자신의 판매 견적 요청서 자재별 총 금액 가져오기 API
 *  @function getSaleSelfTotalApi
 *  @param {number} rfq_number_id rfq number id
 */
export const getSaleSelfTotalApi = ({
  rfq_number_id,
}: getSaleSelfPaylaod): Promise<
  AxiosResponse<
    QuotationSaleSelfGetSaleQuotationTotalPriceSelfData,
    QuotationSaleSelfGetSaleQuotationTotalPriceSelfError
  >
> => {
  return API.get(`/quotation/sale/self/${rfq_number_id}/total`);
};

/**
 *  자신의 판매 견적 요청서 삭제 API
 *  @function deleteSaleSelfApi
 *  @param {number} rfq_number_id rfq number id
 */
export const deleteSaleSelfApi = ({
  rfq_number_id,
}: getSaleSelfPaylaod): Promise<
  AxiosResponse<QuotationSaleSelfDeleteSaleQuotationSelfData, QuotationSaleSelfDeleteSaleQuotationSelfError>
> => {
  return API.delete(`/quotation/sale/self/${rfq_number_id}/delete`);
};

/**
 *  판매 견적 재요청 API
 *  @function createSaleSelfReRequestApi
 *  @param {string} req_close_at 견적 유효기간
 *  @param {string} req_delivery_at 납기 일자
 *  @param {string} shipping_area 상차지
 *  @param {boolean} terms_of_payment 결제조건
 *  @param {string} additional_request_file 첨부파일
 *  @param {string} additional_request_file_name 첨부파일 명
 *  @param {string} seller_note 비고
 *  @param {number[]} item_ids 견적 품목 리스트
 */
export const createSaleSelfReRequestApi = ({
  rfq_number_id,
  req_close_days,
  req_delivery_days,
  shipping_area,
  terms_of_payment,
  additional_request_file,
  additional_request_file_name,
  seller_note,
  item_ids,
}: createSaleSelfReRequestPayload): Promise<
  AxiosResponse<QuotationSaleSelfPostSaleQuotationReRequestData, QuotationSaleSelfPostSaleQuotationReRequestError>
> => {
  return API.post(`/quotation/sale/self/${rfq_number_id}/re-request`, {
    req_close_days,
    req_delivery_days,
    shipping_area,
    terms_of_payment,
    additional_request_file,
    additional_request_file_name,
    seller_note,
    item_ids,
  });
};

/**
 *  거래문서 파일 다운로드 API - 판매자
 *  @function downloadSaleFileApi
 */
export const downloadSaleFileApi = ({ rfq_number_id, type, sale_quotation_id}: downloadSaleFilePayload) => {
  return API.get(`/quotation/sale/self/${rfq_number_id}/download?type=${type}&sale_quotation_id=${sale_quotation_id}`);
};
